import { useObserver } from "mobx-react"
import { useRouter } from "next/router"
import React, { useState, useEffect, useMemo } from "react"
import styled, { css } from "styled-components"

import ActionBar from "~/components/molecules/ActionBar"
import Dialog from "~/components/molecules/Dialog"
import ApplyCompleteModal from "~/components/organisms/ApplyCompleteModal"
import ScheduleSelectModal from "~/components/organisms/ScheduleSelectModal"
import { RecruitmentStatus, RecruitmentStatusJp, RecruitmentType } from "~/consts"
import { IsMobile, IsPC, Z } from "~/consts/style-mixins"
import { EventSchedule } from "~/entities/EventSchedule"
import { ShortInternshipSchedule } from "~/entities/ShortInternshipSchdule"
import { internalLink } from "~/services/helper"
import { useStores } from "~/stores/StoreProvider"

interface Props {
  id: number
  type: keyof typeof RecruitmentType
  status: keyof typeof RecruitmentStatus
  applicationUrl?: string
  schedules?: ShortInternshipSchedule[] | EventSchedule[]
  curated?: boolean
  className?: string
}

const ObservedActionBar = ({ id, type, status, applicationUrl, schedules, curated }: Props) => {
  const { userStore, applicationStore, favoriteStore } = useStores()
  const [authPromptModalOpen, toggleAuthPrompModal] = useState(false)
  const [confirmApplyModalOpen, toggleConfirmApplyModal] = useState(false)
  const [selectScheduleModalOpen, toggleSelectScheduleModal] = useState(false)
  const [selectedScheduleId, updateSelectedScheduleId] = useState(0)
  const [completeApplyModalOpen, toggleCompleteApplyModal] = useState(false)
  const router = useRouter()

  const eventEntryIds = useObserver(() => {
    if (type !== "event") {
      return undefined
    }
    return applicationStore.eventScheduleIds
  })

  const isApplied = useObserver(() => {
    if (type === "event") {
      return false
    }
    return applicationStore.isApplied(id, type)
  })

  useEffect(() => {
    userStore.fetchLoginUser().then(() => {
      if (userStore.authedAsStudent) {
        applicationStore.fetch()
        favoriteStore.fetch()
      }
    })
  }, [])

  useEffect(() => {
    switch (router.query.openModal) {
      case "selectSchedule":
        toggleSelectScheduleModal(true)
        break
      case "confirmApply":
        toggleConfirmApplyModal(true)
        break
      case "applyComplete":
        toggleCompleteApplyModal(true)
        break
    }
  }, [router.query.openModal])

  const generateRegistrationUrl = (): string => {
    const basePath = `/auth/register?redirect_after_auth=${location.pathname}`
    const registrationUrl =
      type === "shortInternship" || type === "event"
        ? `${basePath}&openModal=selectSchedule`
        : `${basePath}&openModal=confirmApply`
    return internalLink(registrationUrl)
  }
  const buttonLabel = useMemo((): string => {
    if (isApplied) {
      return "エントリー済み"
    } else if (status === RecruitmentStatus.filled || status === RecruitmentStatus.private) {
      return RecruitmentStatusJp[status]
    } else {
      return "エントリーする"
    }
  }, [isApplied])
  const onClickFavorite = () => {
    const isFavorited = favoriteStore.isFavorited(id, type, curated)
    if (userStore.user) {
      favoriteStore.toggleFavorite(type, id, isFavorited, curated)
    } else {
      toggleAuthPrompModal(true)
    }
  }

  return (
    <>
      {useObserver(() => (
        <ActionBarWrapper>
          <ActionBar
            buttonLabel={buttonLabel}
            onClickButton={() => {
              if (!userStore.user) {
                toggleAuthPrompModal(true)
                return
              }

              if (applicationUrl !== undefined) {
                window.open(applicationUrl)
                return
              }

              if (type === "shortInternship" || type === "event") {
                toggleSelectScheduleModal(true)
              } else {
                toggleConfirmApplyModal(true)
              }
            }}
            onClickIcon={() => onClickFavorite()}
            isFavorited={favoriteStore.isFavorited(id, type, curated)}
            disabled={status === RecruitmentStatus.filled || status === RecruitmentStatus.private || isApplied}
          />
        </ActionBarWrapper>
      ))}

      {/* 登録/ログイン促しモーダル */}
      <Dialog
        open={authPromptModalOpen}
        heading="登録またはログインが必要です"
        mainAction={{
          label: "登録する",
          action: () => window.open(generateRegistrationUrl(), "_self"),
        }}
        subAction={{
          label: "キャンセル",
          action: () => toggleAuthPrompModal(false),
        }}
      >
        エントリー/お気に入りを利用するためには、新規登録またはログインが必要です。
      </Dialog>

      {/* エントリー確認モーダル*/}
      <Dialog
        open={confirmApplyModalOpen}
        heading="エントリーの確認"
        mainAction={{
          label: "エントリー",
          action: async () => {
            toggleConfirmApplyModal(false)
            await applicationStore.apply(type, id)
            toggleCompleteApplyModal(true)
            router.push({
              pathname: location.pathname,
              query: {
                openModal: "applyComplete",
              },
            })
          },
        }}
        subAction={{
          label: "キャンセル",
          action: () => toggleConfirmApplyModal(false),
        }}
      >
        この募集にエントリーします。本当によろしいですか？
      </Dialog>

      {/* 日程選択モーダル */}
      {schedules !== undefined && selectScheduleModalOpen && (
        <ScheduleSelectModal
          open
          schedules={schedules}
          updateSelectedScheduleId={updateSelectedScheduleId}
          mainAction={{
            label: "エントリー",
            action: async () => {
              toggleSelectScheduleModal(false)
              await applicationStore.apply(type, id, selectedScheduleId)
              router.push({
                pathname: location.pathname,
                query: {
                  openModal: "applyComplete",
                },
              })
            },
          }}
          subAction={{
            label: "キャンセル",
            action: () => toggleSelectScheduleModal(false),
          }}
          entryIds={eventEntryIds}
        />
      )}

      {/* 応募完了モーダル */}
      <ApplyCompleteModal
        open={completeApplyModalOpen}
        recruitmentType={type}
        mainAction={{
          label: "申し込みを確認する",
          action: () => window.open(`${process.env.INFRA_INTERN_URL}/my-page`),
        }}
        subAction={{
          label: "閉じる",
          action: () => toggleCompleteApplyModal(false),
        }}
      />
    </>
  )
}

const ActionBarWrapper = styled.div`
  max-width: 1000px;
  text-align: center;
  z-index: ${Z.Child};
  ${IsPC(css``)}
  ${IsMobile(css`
    padding: 0 16px;
  `)}
`

export default ObservedActionBar
